import React from 'react';
import {
    Box,
    Button,
    Card,
    Divider,
    Grid,
    List,
    ListItem,
    ListItemSecondaryAction,
    ListItemText,
    Typography,
} from '@material-ui/core';
import { observer } from 'mobx-react-lite';
import { format } from 'date-fns';
import { useContext, useEffect, useState } from 'react';
import {
    BillType,
    IBill,
    IdBill,
    IdLeaseAgreement,
    IDTransactionLog,
    ILeaseAgreement,
    IProfile,
    ITransactionLog,
    IWallet,
    TransactionStatus,
    TrxLogType,
} from 'realhaus-sdk';
import { NumericFormat } from 'react-number-format';
import { RootStoreContext } from '../../global/storeContext';
import { DocumentData, QueryDocumentSnapshot } from 'firebase/firestore';
import { DEFAULT_TRANSACTIONS_LIMIT } from '../../stores/walletStore';
import { useStyles } from './styles';
import StringUtils from '../../utils/string';
import { useIsMobile } from '../uiComponents/UIComponents';
import { IsSystemWallet } from '../../global/util';
import { RightDrawer } from '../uiComponents/rightDrawer';

const CurrencyFormatCAD = ({ amount }: { amount: number }) => {
    return (
        <NumericFormat
            value={amount}
            prefix='$ '
            suffix=' CAD'
            displayType={'text'}
            thousandSeparator={true}
            decimalSeparator='.'
            fixedDecimalScale
            decimalScale={2}
        />
    );
};

type Activity = { trx: IDTransactionLog; bill?: IdBill; lease?: IdLeaseAgreement };
const isReceiver = (trnx: IDTransactionLog, userId: string) => trnx.receiverId === userId;
const isSender = (trnx: IDTransactionLog, userId: string) => trnx.senderId === userId;

const isCreditReportSeatTrnx = (trnx: ITransactionLog) => !!trnx.meta?.creditReportSeatId;
const getTrnxSummaryText = (
    activity: Activity,
    isReceiver: (trx: IDTransactionLog) => boolean,
    isSender: (trx: IDTransactionLog) => boolean,
    userProfiles: Map<String, IProfile>,
    purpose: boolean = false,
    full: boolean = false,
) => {
    const trnx = activity.trx;

    if (trnx.type === TrxLogType.PAYMENT) {
        if (!!trnx.meta?.billId && !!trnx.meta?.leaseId) {
            const lease = activity.lease;
            const bill = activity.bill;

            let billType = 'Bill';
            if (!!bill) {
                switch (bill.type) {
                    case BillType.OTHER_FEE: {
                        billType = 'Bill';
                        break;
                    }
                    default: {
                        billType = bill.type.replaceAll('_', ' ');
                        break;
                    }
                }
            }

            let name;
            const sender = userProfiles.get(trnx.senderId);
            if (!!sender) {
                name = `${sender.firstname} ${sender.lastname}`;
            }

            if (purpose) {
                return isSender(trnx)
                    ? `Sent ${billType} Payment`
                    : isReceiver(trnx)
                    ? `Received ${billType} Payment`
                    : '';
            } else {
                if (isReceiver(trnx)) {
                    if (name && full) {
                        return `Received ${billType} Payment from ${name}`;
                    }

                    return !!name
                        ? `Received ${billType} Payment from ${name}`
                        : `Received ${billType} Payment`;
                } else if (isSender(trnx)) {
                    return `Sent ${billType} Payment`;
                } else {
                    return 'N/A';
                }
            }
        } else if (isCreditReportSeatTrnx(trnx) && isSender(trnx)) {
            return full
                ? `Payment for ${trnx.meta?.numberOfSeats ?? ''} credit report seat(s)`
                : purpose
                ? 'Credit Report Seats Purchase'
                : `Purchased ${trnx.meta?.numberOfSeats ?? ''} credit report seat(s)`;
        }
    } else if (trnx.type === TrxLogType.WITHDRAW) {
        return trnx.narration;
    } else if (trnx.type === TrxLogType.TRANSFER) {
        if (isReceiver(trnx)) {
            // received bill payment
            return 'Received Funds Transfer';
        } else if (isSender(trnx)) {
            return IsSystemWallet(trnx.receiverId) ? trnx.narration : 'Transferred Funds';
        } else {
            return 'N/A';
        }
    }

    console.warn('Unrecognized transactionn type');
    console.log(trnx);

    return trnx.narration;
};

export const AccountTransactionsView: React.FC<{
    transactions: IDTransactionLog[];
    canFetchMoreTransaction: boolean;
    getTransactions: () => Promise<void>;
}> = observer(({ transactions, canFetchMoreTransaction, getTransactions }) => {
    const [openTransactionDetails, setOpenTransactionDetails] = React.useState<boolean>(false);
    const [selectedTransaction, setSelectedTransaction] = React.useState<Activity>();
    const [userProfiles] = React.useState<Map<String, IProfile>>(new Map());
    const [bills] = React.useState<Map<String, IBill>>(new Map());
    const [leases] = React.useState<Map<String, ILeaseAgreement>>(new Map());
    const [loaded, setLoaded] = React.useState<boolean>(false);

    const [activities] = React.useState<Map<String, Activity>>(new Map());

    useEffect(() => {
        setLoaded(false);
        if (!transactions) return;

        const userIds: string[] = [];
        const leaseIds: string[] = [];
        const billIds: { leaseId: string; billId: string }[] = [];

        const userProfilePromises: Promise<void>[] = [];
        const billPromises: Promise<void>[] = [];
        const leasePromises: Promise<void>[] = [];

        transactions.map(async (t) => {
            const fetchUserProfile = async (uid: string) => {
                try {
                    const p = await userStore.getUserProfile(uid);
                    if (!!p) userProfiles?.set(t.senderId, p);
                } catch (err) {
                    console.log(err);
                }
            };

            let uId;
            if (
                isReceiver(t, userStore.userId!) &&
                !IsSystemWallet(t.senderId) &&
                !userProfiles?.has(t.senderId) &&
                !userIds.find((uId) => uId === t.senderId)
            ) {
                uId = t.senderId;
            } else if (
                isSender(t, userStore.userId!) &&
                !IsSystemWallet(t.receiverId) &&
                !userProfiles?.has(t.receiverId) &&
                !userIds.find((uId) => uId === t.receiverId)
            ) {
                uId = t.receiverId;
            }

            if (uId) {
                userIds.push(uId);
                userProfilePromises.push(fetchUserProfile(uId));
            }

            const fetchLease = async (leaseId: string) => {
                const lease = await leaseStore.getLeaseAgreementById(leaseId);
                if (!!lease) {
                    leases.set(leaseId, lease);
                }
            };

            const fetchBill = async (leaseId: string, billId: string) => {
                try {
                    const b = await leaseStore.getLeaseBill(leaseId, billId);
                    if (b) {
                        bills.set(billId, b);
                    }
                } catch (err) {
                    console.log(err);
                }
            };

            if (!!t.meta?.leaseId && !leaseIds.find((l) => l === t.meta?.leaseId)) {
                leaseIds.push(t.meta.leaseId);
                leasePromises.push(fetchLease(t.meta.leaseId));
            }

            if (!!t.meta?.leaseId && !!t.meta?.billId) {
                if (
                    !bills?.has(t.meta.billId) &&
                    !billIds.find(
                        (b) => b.billId === t.meta?.billId && b.leaseId === t.meta.leaseId,
                    )
                ) {
                    billIds.push({ leaseId: t.meta.leaseId, billId: t.meta.billId });
                    billPromises.push(fetchBill(t.meta.leaseId, t.meta.billId));
                }
            }
        });

        Promise.allSettled([...userProfilePromises, ...billPromises, ...leasePromises])
            .then(() => {
                setLoaded(true);

                transactions.forEach((t) => {
                    const activity: Activity = {
                        trx: t,
                    };

                    if (!!t.meta?.leaseId) {
                        const l = leases.get(t.meta.leaseId);
                        if (l) {
                            activity.lease = { id: t.meta.leaseId, ...l };
                        }
                    }

                    if (!!t.meta?.billId) {
                        const b = bills.get(t.meta.billId);
                        if (!!b) {
                            activity.bill = { id: t.meta.billId, ...b };
                        }
                    }
                    activities.set(t.id, activity);
                });
            })
            .catch(() => {});
    }, [transactions]);

    const handleCloseTransactionDetails = () => {
        setOpenTransactionDetails(false);
        setSelectedTransaction(undefined);
    };

    const onOpenTransactionDetailsDrawer = () => {
        setOpenTransactionDetails(true);
    };

    const handleOpenTransactionDetails = (activity: Activity) => {
        setSelectedTransaction(activity);
        onOpenTransactionDetailsDrawer();
    };

    const classes = useStyles();
    const isMobile = useIsMobile();
    const { userStore, leaseStore } = React.useContext(RootStoreContext);

    const trxStatusColor = (status: TransactionStatus) => {
        switch (status) {
            case TransactionStatus.SUCCESS:
                return 'green';
            case TransactionStatus.FAILED:
                return 'red';
            default:
                return '#ff9e00';
        }
    };

    const TrnxAmount = ({ trnx }: { trnx: IDTransactionLog }) => {
        let amount = trnx.amount;
        if (isReceiver(trnx, userStore.userId!) && trnx.type === TrxLogType.PAYMENT) {
            amount = trnx.amount - (trnx.meta?.trxfees ?? 0);
        }

        return <CurrencyFormatCAD amount={amount} />;
    };
    return (
        <>
            <Card variant='outlined' elevation={0}>
                <Box className={classes.container} key='wallet-view'>
                    <List>
                        {
                            
                            Array.from(activities.values()).map((activity) => (
                                    <div
                                        onClick={() => handleOpenTransactionDetails(activity)}
                                        key={activity.trx.id}
                                    >
                                        <ListItem key={activity.trx.id}>
                                            <ListItemText
                                                primary={
                                                    <Typography variant='body2'>
                                                        {format(activity.trx.timestamp, 'PPp')}
                                                    </Typography>
                                                }
                                                secondary={
                                                    <>
                                                        <Typography
                                                            variant='body1'
                                                            noWrap
                                                            style={{ color: 'black' }}
                                                        >
                                                            {isMobile
                                                                ? StringUtils.truncateString(
                                                                      getTrnxSummaryText(
                                                                          activity,
                                                                          (trx: IDTransactionLog) =>
                                                                              isReceiver(
                                                                                  trx,
                                                                                  userStore.userId!,
                                                                              ),
                                                                          (trx: IDTransactionLog) =>
                                                                              isSender(
                                                                                  trx,
                                                                                  userStore.userId ??
                                                                                      '',
                                                                              ),
                                                                          userProfiles,
                                                                      ),
                                                                      22,
                                                                  )
                                                                : StringUtils.truncateString(
                                                                      getTrnxSummaryText(
                                                                          activity,
                                                                          (trx: IDTransactionLog) =>
                                                                              isReceiver(
                                                                                  trx,
                                                                                  userStore.userId!,
                                                                              ),
                                                                          (trx: IDTransactionLog) =>
                                                                              isSender(
                                                                                  trx,
                                                                                  userStore.userId ??
                                                                                      '',
                                                                              ),
                                                                          userProfiles,
                                                                      ),
                                                                  )}
                                                        </Typography>
                                                        <Typography variant='caption'>
                                                            {activity.trx.id}
                                                        </Typography>
                                                    </>
                                                }
                                            />
                                            <ListItemSecondaryAction>
                                                <ListItemText
                                                    primary={
                                                        <Typography
                                                            variant='body1'
                                                            style={{ fontWeight: 'bold' }}
                                                        >
                                                            <TrnxAmount trnx={activity.trx} />
                                                        </Typography>
                                                    }
                                                    secondary={
                                                        <Typography
                                                            variant='body1'
                                                            noWrap
                                                            style={{
                                                                color: trxStatusColor(
                                                                    activity.trx.status,
                                                                ),
                                                            }}
                                                        >
                                                            {activity.trx.status}
                                                        </Typography>
                                                    }
                                                ></ListItemText>
                                            </ListItemSecondaryAction>
                                        </ListItem>
                                        <Divider component="li" key={`${activity.trx.id}-divider`}/>
                                    </div>


                              ))
                        }
                        <ListItem
                            button
                            onClick={getTransactions}
                            disabled={!canFetchMoreTransaction}
                            key='load-more'
                        >
                            <ListItemText inset primary=' Load more transactions...' />
                        </ListItem>
                    </List>
                </Box>
            </Card>

            <TransactionDetailDrawer
                handleClose={handleCloseTransactionDetails}
                isOpen={openTransactionDetails}
                onOpen={onOpenTransactionDetailsDrawer}
                transaction={selectedTransaction}
                userProfiles={userProfiles}
            />
        </>
    );
});

export const TransactionDetailDrawer: React.FC<{
    handleClose: () => void;
    isOpen: boolean;
    onOpen: () => void;
    transaction?: Activity;
    userProfiles: Map<String, IProfile>;
}> = observer(({ handleClose, isOpen, transaction, onOpen, userProfiles }) => {
    const styles = useStyles();
    const { walletStore, uiStore, userStore } = React.useContext(RootStoreContext);
    const [transactionLog, setTransactionLog] = React.useState<ITransactionLog>();
    const [disableButton, setDisableButton] = React.useState<boolean>(false);

    const handleSendWithdrawalInstruction = () => {
        if (!transaction) return;

        uiStore.showLoading();

        walletStore
            .resendWithdrawalInstruction(transaction.trx.id)
            .then((res) => {
                if (!!res) {
                    uiStore.success('Withdrawal instruction sent');
                    handleDisableWithdrawButton();
                    return;
                }
            })
            .catch((err) => {
                console.error(err);
                uiStore.error('Failed to send withdrawal instruction');
            })
            .finally(() => {
                uiStore.hideLoading();
            });
    };

    const handleDisableWithdrawButton = () => {
        setDisableButton(true);
        setTimeout(() => {
            setDisableButton(false);
        }, 60000);
    };

    const trxSummary =
        transaction &&
        getTrnxSummaryText(
            transaction,
            (trx: IDTransactionLog) => isReceiver(trx, userStore.userId!),
            (trx: IDTransactionLog) => isSender(trx, userStore.userId ?? ''),
            userProfiles,
            false,
            true,
        );

    const trxPurpose =
        transaction &&
        getTrnxSummaryText(
            transaction,
            (trx: IDTransactionLog) => isReceiver(trx, userStore.userId!),
            (trx: IDTransactionLog) => isSender(trx, userStore.userId ?? ''),
            userProfiles,
            true,
        );

    useEffect(() => {
        if (!!transaction) {
            setTransactionLog(transaction.trx);
        }
    }, [transaction]);

    return (
        <>
            <RightDrawer isOpen={isOpen} onOpen={onOpen} onClose={handleClose}>
                <Box mb={3}>
                    <Typography variant='h4'>Transaction Details</Typography>
                </Box>

                <Box
                    mb={3}
                    style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                    }}
                >
                    <Typography className={styles.textSecondary}>
                        ID: {transaction?.trx?.id}
                    </Typography>

                    {transactionLog?.type === TrxLogType.WITHDRAW &&
                        transactionLog.status === TransactionStatus.PROCESSING && (
                            <>
                                <Box>
                                    <Button
                                        variant='contained'
                                        color='primary'
                                        disabled={disableButton}
                                        onClick={handleSendWithdrawalInstruction}
                                    >
                                        Resend withdraw instructions
                                    </Button>
                                </Box>
                            </>
                        )}
                </Box>

                <Grid container alignItems='center' className={styles.tranxPaper}>
                    <Grid item xs={5}>
                        <Box padding={3}>
                            <Typography style={{ paddingBottom: '10px' }}>
                                <b>STATUS</b>
                            </Typography>
                            <Typography variant='subtitle1'>{transactionLog?.status}</Typography>
                        </Box>
                    </Grid>

                    <Divider orientation='vertical' flexItem />

                    <Grid item xs={5}>
                        <Box padding={3}>
                            <Typography style={{ paddingBottom: '10px' }}>
                                <b>TRANSACTION DATE</b>
                            </Typography>
                            <Typography variant='subtitle1'>
                                {!!transaction
                                    ? StringUtils.getDateTimeFormat(transaction?.trx?.timestamp)
                                    : '-'}
                            </Typography>
                        </Box>
                    </Grid>

                    <Grid item xs={12}>
                        <Divider />
                    </Grid>

                    <Grid item xs={5}>
                        <Box padding={3}>
                            <Typography style={{ paddingBottom: '10px' }}>
                                <b>PURPOSE</b>
                            </Typography>
                            <Typography variant='subtitle1'>{trxPurpose}</Typography>
                        </Box>
                    </Grid>
                    <Divider orientation='vertical' flexItem />
                    <Grid item xs={5}>
                        <Box padding={3}>
                            <Typography style={{ paddingBottom: '10px' }}>
                                <b>AMOUNT</b>
                            </Typography>
                            <Typography variant='subtitle1'>
                                {StringUtils.asCurrency(transaction?.trx?.amount ?? 0)}
                            </Typography>
                        </Box>
                    </Grid>

                    <Grid item xs={12}>
                        <Divider />
                    </Grid>

                    {transaction?.trx.type !== TrxLogType.WITHDRAW && (
                        <>
                            <Grid item xs={5}>
                                <Box padding={3}>
                                    <Typography style={{ paddingBottom: '10px' }}>
                                        <b>PAYMENT METHOD</b>
                                    </Typography>
                                    <Typography variant='subtitle1'>
                                        {transactionLog?.meta?.paymentMethod ?? 'N/A'}
                                    </Typography>
                                </Box>
                            </Grid>
                            <Divider orientation='vertical' flexItem />
                        </>
                    )}
                    <Grid item xs={5}>
                        <Box padding={3}>
                            <Typography style={{ paddingBottom: '10px' }}>
                                <b>OTHER INFO</b>
                            </Typography>
                            <Typography variant='subtitle1'>{trxSummary}</Typography>
                        </Box>
                    </Grid>
                </Grid>
            </RightDrawer>
        </>
    );
});

export const WalletComponent: React.FC = observer(() => {
    const { uiStore, walletStore } = useContext(RootStoreContext);
    const [wallet, setWallet] = useState<IWallet | undefined>();
    const [trxRecords, setTrxRecords] = useState<IDTransactionLog[]>([]);
    const [lastQueryDoc, setLastQueryDoc] =
        React.useState<QueryDocumentSnapshot<DocumentData> | null>(null);
    const [canFetchMoreTransactions, setCanFetchMoreTransactions] = React.useState<boolean>(false);

    const getTrxActivities = async () => {
        uiStore.showLoading();
        try {
            // fetch transactions
            const activities = await walletStore.getTrxActivities(lastQueryDoc);
            setCanFetchMoreTransactions(
                !!activities && activities.transactions.length === DEFAULT_TRANSACTIONS_LIMIT,
            );

            if (!activities || activities.transactions.length === 0) {
                return;
            }

            setLastQueryDoc(activities.lastDoc);
            const t = activities.transactions;
            setTrxRecords([...trxRecords, ...t]);
        } catch (err) {
            console.error('== Error fetching transaction activities');
            console.error(err);
        } finally {
            uiStore.hideLoading();
        }
    };

    const getWallet = async () => {
        const w = await walletStore.getWallet();
        setWallet(w);
    };
    useEffect(() => {
        const init = async () => {
            uiStore.showLoading();
            try {
                // fetch wallet
                await Promise.allSettled([getWallet(), getTrxActivities()]);
            } finally {
                uiStore.hideLoading();
            }
        };

        init();
    }, []);

    return (
        <>
            <Grid
                container
                justifyContent='space-between'
                style={{ marginTop: '24px', marginBottom: '40px' }}
                spacing={2}
            >
                <Grid item>
                    <div>
                        <Typography variant='body1' color='textSecondary' display='inline'>
                            BALANCE:
                        </Typography>
                        <Typography
                            variant='body1'
                            color='primary'
                            style={{ paddingLeft: '10px', fontWeight: 'bold' }}
                            display='inline'
                        >
                            <strong>
                                <CurrencyFormatCAD amount={wallet?.amount ?? 0} />
                            </strong>
                        </Typography>
                    </div>
                </Grid>
            </Grid>
            <Grid container spacing={3}>
                <Grid item xs={12}>
                    <Typography variant='h6'>Recent Transactions</Typography>
                </Grid>
                <Grid item xs={12}>
                    <AccountTransactionsView
                        transactions={trxRecords}
                        canFetchMoreTransaction={canFetchMoreTransactions}
                        getTransactions={getTrxActivities}
                    />
                </Grid>
            </Grid>
        </>
    );
});
